import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faLock } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarEmpty, faUnlock } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { DATE_DISPLAY_FORMAT, DATE_REQUEST_FORMAT } from '@ess/constants/api';

import FlexBox from '@ess/ui/FlexBox';
import Checkbox from '@ess/ui/Form/Checkbox';
import Anchor from '@ess/ui/Anchor';
import Tooltip from '@ess/ui/Tooltip';

import BasketActionsMenu from '@basket/components/BasketActions';

import { isMobileOnly } from 'react-device-detect';
import { Styled } from '../BasketList.styles';

type BasketItemProps = {
  data: {
    id: number;
    name: string;
    blocked: boolean;
    count: number;
    lastModified: {
      date: string;
      time: string;
    }
    consultant: string;
    description: string;
  }
  isChecked: boolean;
  isFavourite: boolean | undefined;
  onCheckboxChange: (isChecked: boolean, basket: { id: number, name: string }) => void;
  onStarClick: (isChecked: boolean, basket: { id: number, name: string }) => void;
  onNameClick: (basket: { id: number, name: string }) => void;
  onDropdownOpen: ((basketId: number | null) => void) | undefined;
  onAction: ((action: string, data: any) => any) | undefined;
  onLockClick: (basket: { id: number, name: string, blocked: boolean }) => void;
}

const BasketItem = ({
  data,
  isChecked,
  isFavourite = false,
  onNameClick,
  onStarClick,
  onAction = undefined,
  onCheckboxChange,
  onDropdownOpen = undefined,
  onLockClick,
}: BasketItemProps) => {
  const { t } = useTranslation();

  const onBasketNameClickHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    onNameClick(data);
  };

  const onCheckboxChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheckboxChange(event.target.checked, data);
  };

  const onFavouritesChangeHandler = () => {
    onStarClick(!isFavourite, data);
  };

  const onLockClickHandler = () => {
    onLockClick(data);
  };

  return (
    <Styled.BasketItem
      isFavourite={!!isFavourite}
      isChecked={isChecked}
    >
      <FlexBox
        flexGrow={1}
        mr="5px"
        flexDirection="column"
        style={{
          gap: '10px',
        }}
      >
        <FlexBox
          width="100%"
          flexWrap="nowrap"
        >
          <FlexBox
            flexGrow={1}
          >
            <Anchor
              color="textColor"
              style={{ overflowWrap: 'anywhere', fontWeight: 'bold' }}
              onClick={onBasketNameClickHandler}
            >
              {data.name}
            </Anchor>
          </FlexBox>
          <FlexBox
            flexShrink={0}
            style={{
              textAlign: 'center',
            }}
          >
            {' '}
            {t('breadcrumb_results')}
            {': '}
            {data.count}
          </FlexBox>
        </FlexBox>
        <FlexBox
          width="100%"
          flexWrap="nowrap"
        >
          <FlexBox
            flexGrow={1}
          >
            <FlexBox style={{ textAlign: 'center' }}>
              {`${moment(data.lastModified.date, DATE_REQUEST_FORMAT).format(DATE_DISPLAY_FORMAT)} ${moment(data.lastModified.time, 'HH:mm:ss').format('HH:mm')}`}
            </FlexBox>
            {isFavourite && (
              <FontAwesomeIcon
                icon={faStar}
                style={{
                  marginLeft: '4px',
                }}
              />
            )}
            {data.blocked && (
              <FontAwesomeIcon
                icon={faLock}
                style={{
                  marginLeft: '4px',
                }}
              />
            )}
          </FlexBox>
          <FlexBox
            marginLeft="auto"
            flexShrink={0}
            style={{
              textAlign: 'right',
            }}
          >
            {t('lbl_expedient_short')}
            {': '}
            {data.consultant}
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <FlexBox
        flexShrink="0"
      >
        <BasketActionsMenu
          basket={data}
          isChecked={isChecked}
          onCheckboxChange={onCheckboxChange}
          onDropdownOpen={onDropdownOpen}
          onAction={onAction}
          excludedActions={['autoCheckStatus', 'deleteOffers', 'createBasket']}
        />
      </FlexBox>
    </Styled.BasketItem>
  );
};

export default BasketItem;
